import { useState, useEffect } from "react";
import api from "../api/api";

// Pantalla de cobros del prestador (Paso 6 — onboarding del destino).
//
// Cierra una promesa que el sistema YA HACIA: el email `payoutEjecutado`
// dice "Ver mis pagos →" y apuntaba a /prestador/pagos, que no existia.
//
// Sigue el patron de ProviderProfilePage: JSX con estilos inline y un
// SectionCard local. NO usa MUI — solo 2 paginas del proyecto lo usan
// (RatingPage y ServiceRequestPage), y el vecindario de /prestador manda
// sobre la convencion documentada.
//
// ⚠ El CLAUDE.md raiz dice que la UI es MUI. No es cierto en la practica.
// Ver DEUDA-FRONTEND-CLAUDEMD-DICE-MUI-Y-CASI-NADIE-LO-USA: o el documento
// esta desactualizado o hay una migracion a medias, y quien lea el documento
// para escribir la proxima pantalla la va a escribir en el estilo
// equivocado.

const font = "Montserrat, sans-serif";

const inputStyle = {
  padding: "10px 14px", borderRadius: 9, border: "1.5px solid rgba(0,0,0,0.12)",
  fontSize: 14, fontFamily: font, color: "#163244",
  background: "#fff", outline: "none", width: "100%", boxSizing: "border-box",
  transition: "border-color 0.2s",
};

const labelStyle = {
  display: "block", fontSize: 10, fontWeight: 700, color: "#7a93a4",
  textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6,
};

// Colores por estado de payout. Las CLAVES son el status interno, pero el
// TEXTO que se muestra NO sale de aca: viene de `estado_visible`, que arma el
// backend. El mapeo estado -> texto vive en un solo lugar a proposito, para
// que mobile no lo duplique cuando agregue esta pantalla.
const COLOR_PAYOUT = {
  paid:         { color: "#1b7f4e", bg: "#e8f7ef" },
  submitted:    { color: "#b06a00", bg: "#fff3e0" },
  pending:      { color: "#5c7a8a", bg: "#f0f5f8" },
  failed:       { color: "#c0392b", bg: "#fdecea" },
  needs_review: { color: "#c0392b", bg: "#fdecea" },
};

function SectionCard({ title, subtitle, action, children }) {
  return (
    <div style={{ background: "#fff", borderRadius: 16, border: "1px solid rgba(0,0,0,0.07)", marginBottom: 18, overflow: "hidden" }}>
      <div style={{ padding: "16px 24px", borderBottom: "1px solid rgba(0,0,0,0.06)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15, color: "#163244" }}>{title}</div>
          {subtitle && <div style={{ fontSize: 12, color: "#7a93a4", marginTop: 2 }}>{subtitle}</div>}
        </div>
        {action}
      </div>
      <div style={{ padding: "22px 24px" }}>{children}</div>
    </div>
  );
}

function Aviso({ tono = "info", children }) {
  const tonos = {
    info:  { bg: "#f0f5f8", border: "#5c7a8a", color: "#163244" },
    ok:    { bg: "#e8f7ef", border: "#1b7f4e", color: "#12603a" },
    warn:  { bg: "#fff3e0", border: "#E7A432", color: "#7a4f00" },
    error: { bg: "#fdecea", border: "#c0392b", color: "#8c2a1e" },
  };
  const t = tonos[tono] || tonos.info;
  return (
    <div style={{
      background: t.bg, borderLeft: `4px solid ${t.border}`, borderRadius: 8,
      padding: "12px 16px", fontSize: 13, color: t.color, lineHeight: 1.5,
    }}>
      {children}
    </div>
  );
}

// Dialogo de confirmacion para reemplazar un destino YA VERIFICADO.
//
// Cambiar el email tiene una consecuencia real —vuelve al circuito de
// aprobacion manual (V3)— y el prestador tiene que enterarse ANTES de
// confirmar, no cuando su proximo cobro no salga solo.
function ConfirmarReemplazo({ emailAnterior, emailNuevo, onCancelar, onConfirmar, guardando, error }) {
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(22,50,68,0.45)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20,
    }}>
      <div style={{ background: "#fff", borderRadius: 16, maxWidth: 460, width: "100%", padding: "26px 28px", fontFamily: font }}>
        <div style={{ fontWeight: 700, fontSize: 16, color: "#163244", marginBottom: 10 }}>
          Vas a cambiar tu email de cobro
        </div>
        <div style={{ fontSize: 13, color: "#5c7a8a", lineHeight: 1.6, marginBottom: 16 }}>
          <div style={{ marginBottom: 8 }}>
            De <strong style={{ color: "#163244" }}>{emailAnterior}</strong><br />
            a <strong style={{ color: "#163244" }}>{emailNuevo}</strong>
          </div>
        </div>
        <Aviso tono="warn">
          Tu email actual ya está verificado y tus cobros salen automáticamente.
          Al cambiarlo, <strong>el próximo cobro va a necesitar una aprobación
          de FIXO</strong>, por única vez. Después vuelve a ser automático.
        </Aviso>

        {/* El error del guardado se muestra ACA DENTRO, no en el formulario:
            el formulario esta detras del overlay y seria invisible.
            Sin esto, un POST fallido dejaba el modal abierto, el boton
            volviendo de "Guardando…" a "Si, cambiar" y NADA mas — el
            prestador reintentaba a ciegas.
            Y NO se cierra el modal ante el error: cerrarlo haria parecer
            que la accion se cancelo, y no sabria si el cambio se aplico. */}
        {error && (
          <div style={{ marginTop: 14 }}>
            <Aviso tono="error">{error}</Aviso>
          </div>
        )}

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
          <button
            onClick={onCancelar}
            disabled={guardando}
            style={{
              padding: "9px 20px", borderRadius: 50, border: "1.5px solid rgba(0,0,0,0.12)",
              background: "#fff", color: "#5c7a8a", fontFamily: font, fontSize: 13,
              fontWeight: 700, cursor: guardando ? "default" : "pointer",
            }}
          >
            Cancelar
          </button>
          <button
            onClick={onConfirmar}
            disabled={guardando}
            style={{
              padding: "9px 20px", borderRadius: 50, border: "none",
              background: "#E7A432", color: "#163244", fontFamily: font, fontSize: 13,
              fontWeight: 700, cursor: guardando ? "default" : "pointer", opacity: guardando ? 0.6 : 1,
            }}
          >
            {guardando ? "Guardando…" : "Sí, cambiar"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ProviderPaymentsPage() {
  const [cargando, setCargando]   = useState(true);
  const [error, setError]         = useState("");

  const [metodo, setMetodo]           = useState(null);
  const [verificado, setVerificado]   = useState(false);
  const [requiereAprob, setRequiereAprob] = useState(false);
  const [huboProblema, setHuboProblema]   = useState(false);

  const [payouts, setPayouts] = useState([]);

  const [editando, setEditando] = useState(false);
  const [email, setEmail]       = useState("");
  const [guardando, setGuardando] = useState(false);
  const [errorForm, setErrorForm] = useState("");
  const [confirmando, setConfirmando] = useState(false);
  // Mensaje post-guardado. Sale de la respuesta del backend, no se recalcula.
  const [avisoCambio, setAvisoCambio] = useState(null);

  function cargar() {
    setCargando(true);
    setError("");
    Promise.all([
      api.get("/providers/me/payout-methods"),
      api.get("/providers/me/payouts"),
    ])
      .then(([resMetodos, resPayouts]) => {
        const d = resMetodos.data;
        // Todos los estados vienen calculados del backend. No se recalcula
        // nada en el cliente.
        setMetodo(d.payout_method || null);
        setVerificado(!!d.verificado);
        setRequiereAprob(!!d.requiere_aprobacion_manual);
        setHuboProblema(!!d.hubo_problema_ultimo_cobro);
        setEmail(d.payout_method?.destination?.email || "");
        setPayouts(resPayouts.data.payouts || []);
      })
      .catch((err) => {
        console.error("Error cargando cobros:", err);
        setError("No se pudieron cargar tus datos de cobro. Intentá recargar la página.");
      })
      .finally(() => setCargando(false));
  }

  useEffect(cargar, []);

  function intentarGuardar() {
    setErrorForm("");
    const limpio = email.trim();
    // ⚠ COPIA DELIBERADA de la regla del backend, que la aplica con
    // canReceive (payoutGatewayService). Se duplica a proposito: valida sin
    // gastar un round-trip.
    //
    // Pero es una COPIA, no la fuente. La regla canonica vive en canReceive,
    // que es donde el payout la va a aplicar al momento de pagar. Si el
    // backend cambia su regla —por ejemplo al agregar bank_transfer, que
    // valida CVU/CBU y no email— ESTA TIENE QUE SEGUIRLA. Si divergen, el
    // prestador carga un destino que la pantalla acepta y el payout rechaza.
    if (!limpio || !limpio.includes("@")) {
      setErrorForm("Ingresá el email de tu cuenta de Mercado Pago.");
      return;
    }
    if (metodo && limpio === metodo.destination?.email) {
      setErrorForm("Ese ya es tu email de cobro.");
      return;
    }
    // Si el destino actual esta verificado, se confirma antes: el cambio lo
    // devuelve al circuito de aprobacion manual.
    if (verificado) {
      setConfirmando(true);
      return;
    }
    guardar();
  }

  function guardar() {
    setGuardando(true);
    setErrorForm("");
    api.post("/providers/me/payout-method", {
      method: "mp_to_mp",
      destination: { email: email.trim() },
    })
      .then((res) => {
        setConfirmando(false);
        setEditando(false);
        // El aviso sale de los flags del backend, no de lo que el cliente
        // crea que paso.
        setAvisoCambio({
          reemplazoVerificado: !!res.data.reemplazo_un_destino_verificado,
          requiereAprobacion:  !!res.data.requiere_aprobacion_manual,
        });
        cargar();
      })
      .catch((err) => {
        console.error("Error guardando destino:", err);
        setErrorForm(err.response?.data?.error || "No se pudo guardar. Intentá de nuevo.");
      })
      .finally(() => setGuardando(false));
  }

  if (cargando) {
    return (
      <div style={{ fontFamily: font, maxWidth: 760, margin: "0 auto", padding: "28px 20px" }}>
        <div style={{ background: "#fff", borderRadius: 16, padding: 28, textAlign: "center", color: "#8fa3b1", fontSize: 13 }}>
          Cargando tus cobros…
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: font, maxWidth: 760, margin: "0 auto", padding: "28px 20px" }}>
      <div style={{ fontWeight: 700, fontSize: 22, color: "#163244", marginBottom: 4 }}>
        Mis cobros
      </div>
      <div style={{ fontSize: 13, color: "#7a93a4", marginBottom: 22 }}>
        Dónde te transferimos y cómo van tus pagos
      </div>

      {error && <div style={{ marginBottom: 18 }}><Aviso tono="error">{error}</Aviso></div>}

      {/* ── Destino de cobro ────────────────────────────────── */}
      <SectionCard
        title="Destino de cobro"
        subtitle="La cuenta de Mercado Pago donde recibís tus pagos"
        action={
          metodo && !editando ? (
            <button
              onClick={() => { setEditando(true); setErrorForm(""); setAvisoCambio(null); }}
              style={{
                padding: "7px 16px", borderRadius: 50, border: "1.5px solid rgba(0,0,0,0.12)",
                background: "#fff", color: "#163244", fontFamily: font, fontSize: 12,
                fontWeight: 700, cursor: "pointer",
              }}
            >
              Cambiar
            </button>
          ) : null
        }
      >
        {/* Aviso posterior a un cambio. Usa los flags que devolvio el POST. */}
        {avisoCambio && (
          <div style={{ marginBottom: 16 }}>
            <Aviso tono={avisoCambio.reemplazoVerificado ? "warn" : "ok"}>
              {avisoCambio.reemplazoVerificado ? (
                <>
                  Listo, cambiamos tu email de cobro. Como es un destino nuevo,
                  <strong> el próximo cobro vuelve a necesitar una aprobación de
                  FIXO</strong>, por única vez.
                </>
              ) : (
                <>Listo, guardamos tu email de cobro.</>
              )}
            </Aviso>
          </div>
        )}

        {/* ESTADO VACIO — el caso mas comun al principio. Es el punto de
            partida normal, no un error: por eso va en tono info y con una
            instruccion, no con una advertencia. */}
        {!metodo && !editando && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <Aviso tono="info">
                Todavía no cargaste dónde querés cobrar. Ingresá el email de tu
                cuenta de Mercado Pago para poder recibir tus pagos.
              </Aviso>
            </div>
            <button
              onClick={() => setEditando(true)}
              style={{
                padding: "10px 24px", borderRadius: 50, border: "none",
                background: "#E7A432", color: "#163244", fontFamily: font,
                fontSize: 14, fontWeight: 700, cursor: "pointer",
              }}
            >
              Cargar mi email de cobro
            </button>
          </div>
        )}

        {/* DESTINO CARGADO */}
        {metodo && !editando && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <div style={labelStyle}>Email de Mercado Pago</div>
              <div style={{ fontSize: 15, color: "#163244", fontWeight: 600 }}>
                {metodo.destination?.email}
              </div>
            </div>

            {verificado && (
              <Aviso tono="ok">
                <strong>Verificado ✓</strong> — tus cobros salen automáticamente.
              </Aviso>
            )}

            {/* V3: el primer cobro a cada destino lo autoriza un operador. Si
                el prestador no ve esto, cree que algo no funciono. */}
            {requiereAprob && !huboProblema && (
              <Aviso tono="warn">
                {/* Unica cosa que deriva el cliente: si ya hay un cobro en
                    curso, decir "tu primer cobro necesita aprobacion" suena a
                    que no paso nada. Es una decision de PRESENTACION sobre
                    datos que esta vista ya tiene, por eso no va al backend:
                    seria un campo que solo esta pantalla usa.

                    Si mobile hace la misma pantalla y necesita el mismo
                    texto, ahi si conviene subirlo al backend, donde ya vive
                    el resto del mapeo (estado_visible). */}
                {payouts.some((p) => p.status === "pending" || p.status === "submitted") ? (
                  <>Tu primer cobro a este email <strong>está en proceso</strong>. Te avisamos cuando se acredite.</>
                ) : (
                  <><strong>Tu primer cobro a este email necesita una aprobación de FIXO</strong>, por única vez. Después salen automáticamente.</>
                )}
              </Aviso>
            )}

            {/* last_error es prosa en ingles cruda de MP: no se muestra. Solo
                la señal, con una pista accionable y sin culpar al prestador. */}
            {huboProblema && (
              <Aviso tono="error">
                Hubo un problema con tu último cobro. Revisá que el email sea el
                de tu cuenta de Mercado Pago, o escribinos y lo vemos.
              </Aviso>
            )}
          </div>
        )}

        {/* FORMULARIO */}
        {editando && (
          <div>
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle} htmlFor="email-cobro">
                Email de tu cuenta de Mercado Pago
              </label>
              <input
                id="email-cobro"
                style={inputStyle}
                value={email}
                // Se limpia el error al escribir: sin esto, el mensaje
                // ("Ese ya es tu email de cobro") quedaba visible mientras el
                // prestador corregia, y recien desaparecia al apretar Guardar.
                onChange={(e) => { setEmail(e.target.value); setErrorForm(""); }}
                placeholder="tucuenta@email.com"
                autoFocus
              />
              <div style={{ fontSize: 11, color: "#8fa3b1", marginTop: 6 }}>
                Tiene que ser el email con el que entrás a Mercado Pago.
              </div>
            </div>

            {errorForm && <div style={{ marginBottom: 14 }}><Aviso tono="error">{errorForm}</Aviso></div>}

            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={intentarGuardar}
                disabled={guardando}
                style={{
                  padding: "10px 24px", borderRadius: 50, border: "none",
                  background: "#E7A432", color: "#163244", fontFamily: font,
                  fontSize: 14, fontWeight: 700, cursor: guardando ? "default" : "pointer",
                  opacity: guardando ? 0.6 : 1,
                }}
              >
                {guardando ? "Guardando…" : "Guardar"}
              </button>
              <button
                onClick={() => {
                  setEditando(false);
                  setErrorForm("");
                  setEmail(metodo?.destination?.email || "");
                }}
                disabled={guardando}
                style={{
                  padding: "10px 20px", borderRadius: 50, border: "1.5px solid rgba(0,0,0,0.12)",
                  background: "#fff", color: "#5c7a8a", fontFamily: font,
                  fontSize: 14, fontWeight: 700, cursor: "pointer",
                }}
              >
                Cancelar
              </button>
            </div>
          </div>
        )}
      </SectionCard>

      {/* ── Listado de cobros ───────────────────────────────────
          Se oculta SOLO si no hay destino Y no hay cobros. Para un
          prestador nuevo, una tarjeta que dice "todavia no tenes cobros" no
          aporta y compite con la accion que si importa, que es cargar el
          email.

          La condicion es `metodo || payouts.length` y NO solo `metodo`,
          porque los payouts se crean INDEPENDIENTEMENTE del destino: el
          webhook de cobro los inserta por provider_id cuando la transaccion
          pasa a paid, sin mirar provider_payout_methods.

          O sea que un prestador puede tener cobros pendientes SIN haber
          cargado nunca un destino — y hoy ese es el estado de TODOS los
          prestadores reales, porque no existe ningun destino cargado.
          Condicionar solo por `metodo` les escondería plata que les
          corresponde. */}
      {(metodo || payouts.length > 0) && (
      <SectionCard title="Tus cobros" subtitle="Los últimos movimientos">
        {payouts.length === 0 ? (
          <div style={{ fontSize: 13, color: "#8fa3b1", textAlign: "center", padding: "14px 0" }}>
            Todavía no tenés cobros registrados.
          </div>
        ) : (
          <div>
            {payouts.map((p) => {
              const c = COLOR_PAYOUT[p.status] || COLOR_PAYOUT.pending;
              const fecha = p.paid_at || p.failed_at || p.submitted_at || p.created_at;
              return (
                <div
                  key={p.id}
                  style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    padding: "14px 0", borderBottom: "1px solid rgba(0,0,0,0.06)", gap: 12,
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 14, color: "#163244", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {p.service_name || "Servicio FIXO"}
                    </div>
                    <div style={{ fontSize: 11, color: "#8fa3b1", marginTop: 2 }}>
                      {fecha ? new Date(fecha).toLocaleDateString("es-AR") : "—"} · Transacción #{p.transaction_id}
                    </div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#163244" }}>
                      ${Number(p.amount).toLocaleString("es-AR")}
                    </div>
                    {/* estado_visible viene del backend tal cual. */}
                    <span style={{
                      display: "inline-block", marginTop: 4, padding: "3px 10px", borderRadius: 20,
                      fontSize: 10, fontWeight: 700, letterSpacing: "0.04em",
                      color: c.color, background: c.bg,
                    }}>
                      {p.estado_visible}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </SectionCard>
      )}

      {confirmando && (
        <ConfirmarReemplazo
          emailAnterior={metodo?.destination?.email}
          emailNuevo={email.trim()}
          guardando={guardando}
          error={errorForm}
          // Al cancelar se limpia el error: si vuelve a abrir el dialogo,
          // arranca sin el mensaje del intento anterior.
          onCancelar={() => { setConfirmando(false); setErrorForm(""); }}
          // guardar() limpia errorForm al arrancar, asi que se puede
          // reintentar desde el mismo modal sin cerrarlo ni reabrirlo.
          onConfirmar={guardar}
        />
      )}
    </div>
  );
}
